import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import ApiClient from '../client';
import { ENDPOINTS } from '../endpoints';
import { CreateShipmentDto, BulkCreateShipmentDto } from '../types';
import { queryKeys } from '../keys';

const client = new ApiClient(process.env.API_URL!, 'app');

// Queries

export const useProjectShipments = (
  projectId: string,
  pollShipments: boolean = false,
  searchParams?: URLSearchParams,
) => {
  const queryUrl =
    searchParams && searchParams.size > 0
      ? `${ENDPOINTS.PROJECT_SHIPMENTS(projectId)}?${searchParams}`
      : ENDPOINTS.PROJECT_SHIPMENTS(projectId);

  const searchParamsString = searchParams?.toString() || '';
  return useQuery({
    queryKey: queryKeys.projectShipments(projectId, searchParamsString),
    queryFn: () => client.get(queryUrl),
    enabled: !!projectId,
    refetchInterval: pollShipments ? 2000 : false,
    refetchIntervalInBackground: false,
  });
};

export const useShipmentEvents = (shipmentId: string) =>
  useQuery({
    queryKey: queryKeys.shipmentEvents(shipmentId),
    queryFn: () => client.get(ENDPOINTS.SHIPMENT_EVENTS(shipmentId)),
    enabled: !!shipmentId,
  });

export const useProjectShipmentsProcessingStats = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.shipmentProcessingStats,
    queryFn: () => client.get(ENDPOINTS.PROJECT_SHIPMENTS_STATS(projectId)),
    enabled: !!projectId,
    refetchInterval: 10000,
  });

// Mutations
export const useCreateShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateShipmentDto) => client.post(ENDPOINTS.SHIPMENTS, data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      if (data.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectShipments(data.projectId),
        });
      }
    },
  });
};

export const useCreateBulkShipments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkCreateShipmentDto) => client.post(ENDPOINTS.SHIPMENTS_BULK, data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });

      const projectIds = new Set(data.shipments.map((s) => s.projectId).filter((id): id is string => !!id));

      projectIds.forEach((projectId) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectShipments(projectId),
        });
      });
    },
  });
};

export const useDeleteShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) => client.delete(ENDPOINTS.SHIPMENT(shipmentId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};

export const useUpdateShipmentCustomFields = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, customFields }: { shipmentId: string; customFields: object }) =>
      client.patch(ENDPOINTS.SHIPMENT_CUSTOM_FIELDS(shipmentId), customFields),
    onSuccess: (data, { shipmentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shipment(shipmentId) });
      if (data?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectShipments(data.projectId),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};

export const useProjectShipmentsCustomFields = (projectId: string, fieldKey: string) => {
  return useQuery({
    queryKey: queryKeys.projectShipmentsCustomFields(projectId, fieldKey),
    queryFn: () => client.get(ENDPOINTS.PROJECT_SHIPMENTS_CUSTOM_FIELDS(projectId, fieldKey)),
    enabled: !!projectId && !!fieldKey,
  });
};
